import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  FileCheck2,
  FileText,
  History,
  RotateCcw,
  Send,
  ShieldCheck,
} from 'lucide-react';
import './Legal.css';

const STATUS_LABEL = {
  draft: 'Entwurf – nicht öffentlich',
  reviewed: 'Geprüft – bereit zur Aktivierung',
  active: 'Aktive Generation',
  superseded: 'Historische Generation',
};

const asciiSort = (left, right) => (left < right ? -1 : (left > right ? 1 : 0));

async function sha256Hex(value) {
  if (!window.crypto?.subtle) throw new Error('Web-Crypto ist in diesem Browser nicht verfügbar');
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

export function serverManifestProjection(payload) {
  const inherited = payload.manifest_format === 2;
  return {
    ...(inherited ? { manifest_format: 2, inherits_from: {
      release_id: payload.inherits_from?.release_id,
      generation_id: payload.inherits_from?.generation_id,
      manifest_sha256: payload.inherits_from?.manifest_sha256,
    } } : {}),
    release_id: payload.release_id,
    version: payload.version,
    lang: payload.lang,
    effective_at: payload.effective_at,
    documents: [...payload.documents].sort((left, right) => asciiSort(left.doc_type, right.doc_type)).map(document => ({
      doc_type: document.doc_type,
      title: document.title,
      ...(inherited ? { version: document.version, effective_at: document.effective_at } : {}),
      audience: inherited ? [...document.audience].sort(asciiSort) : document.audience,
      visibility: document.visibility,
      required_for: inherited ? [...document.required_for].sort(asciiSort) : document.required_for,
      canonical_path: document.canonical_path,
      content_sha256: document.content_sha256,
    })),
  };
}

export async function verifyPackage(payload) {
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload.documents)) {
    throw new Error('Das Paket muss ein Objekt mit Dokumentliste sein.');
  }
  const documents = await Promise.all(payload.documents.map(async document => {
    const computed = await sha256Hex(String(document.content ?? ''));
    return { type: document.doc_type, expected: document.content_sha256, computed,
      ok: computed === document.content_sha256 };
  }));
  const manifest = await sha256Hex(JSON.stringify(serverManifestProjection(payload)));
  return { documents, manifest, ok: documents.every(document => document.ok) };
}

const initialPackage = JSON.stringify({
  release_id: 'voogo-legal-1.0.0',
  version: '1.0.0',
  lang: 'de',
  effective_at: new Date().toISOString().slice(0, 10),
  documents: [],
}, null, 2);

export default function Legal() {
  const [overview, setOverview] = useState({ docs: [], releases: [], active_generation: null });
  const [selected, setSelected] = useState(null);
  const [packageText, setPackageText] = useState(initialPackage);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/admin/legal');
      setOverview({
        docs: response.data.docs || [],
        releases: response.data.releases || [],
        active_generation: response.data.active_generation || null,
      });
    } catch { toast.error('Legal-Hub-Status konnte nicht geladen werden'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const inspectRelease = async releaseId => {
    setBusy('inspect');
    try {
      const response = await api.get(`/admin/legal/releases/${encodeURIComponent(releaseId)}`);
      setSelected(response.data.release);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Release konnte nicht geladen werden');
    } finally { setBusy(''); }
  };

  const previewPackage = async () => {
    setPreviewError('');
    setPreview(null);
    try {
      const payload = JSON.parse(packageText);
      const verification = await verifyPackage(payload);
      setPreview({ payload, verification });
      if (!verification.ok) toast.error('Mindestens ein Dokument-Hash stimmt nicht');
    } catch (error) { setPreviewError(error.message || 'Ungültiges JSON'); }
  };

  const createDraft = async () => {
    setBusy('create');
    try {
      const payload = JSON.parse(packageText);
      const verification = await verifyPackage(payload);
      if (!verification.ok) throw new Error('Dokument-Hashprüfung fehlgeschlagen');
      const response = await api.post('/admin/legal/releases', payload);
      setSelected(response.data.release);
      setPreview({ payload, verification });
      toast.success('Release-Entwurf gespeichert – noch nicht öffentlich');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Release-Entwurf konnte nicht gespeichert werden');
    } finally { setBusy(''); }
  };

  const review = async () => {
    if (!selected) return;
    if (!window.confirm(`Manifest ${selected.manifest_sha256} als geprüft markieren?\n\nDies veröffentlicht noch nichts.`)) return;
    setBusy('review');
    try {
      const response = await api.post(`/admin/legal/releases/${encodeURIComponent(selected.release_id)}/review`, {
        expected_manifest_sha256: selected.manifest_sha256,
        confirm_review: true,
      });
      setSelected(response.data.release);
      toast.success('Manifest geprüft – weiterhin nicht öffentlich');
      await load();
    } catch (error) { toast.error(error.response?.data?.error || 'Review fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const activate = async () => {
    if (!selected) return;
    const expectedGenerationId = overview.active_generation == null
      ? 0 : overview.active_generation.generation_id;
    if (!Number.isSafeInteger(expectedGenerationId) || expectedGenerationId < 0) {
      return toast.error('Ausgangsgeneration ist nicht eindeutig; Status neu laden.');
    }
    const message = `Release ${selected.release_id} mit 16 Dokumenten atomar aktivieren?\n\nDieser Schritt macht das gesamte Paket öffentlich. Bei geänderten Fahrerbedingungen wird eine E-Mail-Benachrichtigung vorbereitet.`;
    if (!window.confirm(message)) return;
    setBusy('activate');
    try {
      const response = await api.post(`/admin/legal/releases/${encodeURIComponent(selected.release_id)}/activate`, {
        expected_manifest_sha256: selected.manifest_sha256,
        expected_generation_id: expectedGenerationId,
        confirm_activation: true,
      });
      setSelected(response.data.release);
      toast.success(`Generation ${response.data.generation_id} atomar aktiviert`);
      await load();
    } catch (error) { toast.error(error.response?.data?.error || 'Aktivierung fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const rollback = async () => {
    const generation = overview.active_generation;
    if (!generation?.generation_id || !generation?.prior_generation_id) return;
    if (!window.confirm(`Aktive Generation ${generation.generation_id} auf ihre exakte Vorgängergeneration ${generation.prior_generation_id} zurücksetzen?`)) return;
    setBusy('rollback');
    try {
      const response = await api.post('/admin/legal/releases/rollback', {
        expected_generation_id: generation.generation_id,
        confirm_rollback: true,
      });
      setSelected(response.data.release);
      toast.success(`Vorgängergeneration ${response.data.restored_generation_id} wiederhergestellt`);
      await load();
    } catch (error) { toast.error(error.response?.data?.error || 'Rollback fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const activeRelease = overview.releases.find(release => release.status === 'active');
  const selectedDocuments = selected?.documents || [];
  const previewDocuments = preview?.verification.documents || [];
  const previewCounts = useMemo(() => ({
    valid: previewDocuments.filter(document => document.ok).length,
    total: previewDocuments.length,
  }), [previewDocuments]);

  return (
    <div className="legal-root">
      <PageHeader
        title="Rechtstexte"
        description="Ein vollständiges, versioniertes Paket lokal prüfen, als Entwurf speichern und erst nach separatem Review atomar aktivieren."
        actions={
          <div className="legal-heading-badges" aria-label="Legal-Hub-Kontext">
            <span className="is-brand"><ShieldCheck size={13} /> settings.manage</span>
            <span><FileCheck2 size={13} /> SHA-256 Manifest</span>
          </div>
        }
      />

      <section className="legal-boundary" aria-label="Veröffentlichungsgrenze">
        <span className="legal-boundary-icon"><AlertTriangle size={16} /></span>
        <div>
          <strong>Entwurf, Review und Aktivierung sind getrennte Schritte</strong>
          <p>Ein Import veröffentlicht nichts. Nur die ausdrücklich bestätigte Aktivierung verschiebt den öffentlichen Generationszeiger.</p>
        </div>
        <span className="legal-boundary-state"><span /> Fail-closed</span>
      </section>

      <section className="legal-metrics" aria-label="Release-Status">
        <div><span className="legal-metric-icon"><FileText size={16} /></span><span><small>Release-Pakete</small><strong>{overview.releases.length}</strong><em>Entwurf + Historie</em></span></div>
        <div><span className="legal-metric-icon is-coverage"><CheckCircle2 size={16} /></span><span><small>Aktiv</small><strong>{activeRelease?.version || '–'}</strong><em>{activeRelease?.release_id || 'Kein sicheres Paket'}</em></span></div>
        <div><span className="legal-metric-icon is-history"><History size={16} /></span><span><small>Generation</small><strong>{overview.active_generation?.generation_id || '–'}</strong><em>append-only Audit</em></span></div>
        <div><span className="legal-metric-icon"><FileCheck2 size={16} /></span><span><small>Aktive Dokumente</small><strong>{overview.docs.filter(doc => doc.active === 1 && doc.visibility === 'public').length}</strong><em>öffentlich projiziert</em></span></div>
      </section>

      <div className="legal-workspace">
        <section className="legal-editor-card">
          <header><div><small>01 · Offline-Paket</small><h2>JSON-Entwurf</h2></div><span>Import ist nie Publish</span></header>
          <label htmlFor="legal-package">Release-Payload</label>
          <textarea id="legal-package" value={packageText} onChange={event => setPackageText(event.target.value)} rows={24} spellCheck="false" />
          <div className="legal-publish-actions">
            <button type="button" onClick={previewPackage} disabled={!!busy}><Eye size={14} /> Hashes lokal prüfen</button>
            <button type="button" onClick={createDraft} disabled={!!busy || !preview?.verification.ok}><Send size={14} /> Entwurf speichern</button>
          </div>
        </section>

        <aside className="legal-preview-card">
          <header><span className="legal-preview-icon"><Eye size={16} /></span><span><small>02 · Verifikation</small><h2>Manifest-Vorschau</h2></span><em>Kein Publish</em></header>
          {previewError && <div className="legal-preview-empty" role="alert"><AlertTriangle size={20} /><strong>Prüfung fehlgeschlagen</strong><span>{previewError}</span></div>}
          {!preview && !previewError && <div className="legal-preview-empty"><FileText size={20} /><strong>Noch nicht geprüft</strong><span>JSON einfügen und lokale Hashprüfung starten.</span></div>}
          {preview && <article>
            <h3>{preview.payload.release_id}</h3>
            <p><strong>{previewCounts.valid}/{previewCounts.total}</strong> Dokument-Hashes stimmen.</p>
            <p><small>Berechneter Server-Manifest-Hash</small><br /><code data-testid="local-manifest">{preview.verification.manifest}</code></p>
            <div>{previewDocuments.map(document => <p key={document.type}><CheckCircle2 size={13} /> {document.type}: {document.ok ? 'SHA stimmt' : 'SHA ABWEICHEND'}</p>)}</div>
          </article>}
        </aside>
      </div>

      {selected && <section className="legal-publish-bar" aria-label="Ausgewähltes Release">
        <div><span className="legal-publish-icon"><FileCheck2 size={16} /></span><span><small>Ausgewähltes Release</small><strong>{selected.release_id}</strong><em>{STATUS_LABEL[selected.status] || selected.status} · {selectedDocuments.length} Dokumente</em></span></div>
        <div className="legal-publish-actions">
          <code title="Manifest SHA-256">{selected.manifest_sha256}</code>
          {selected.status === 'draft' && <button type="button" onClick={review} disabled={!!busy}><FileCheck2 size={14} /> Review bestätigen</button>}
          {selected.status === 'reviewed' && <button type="button" onClick={activate} disabled={!!busy}><Send size={14} /> Paket aktivieren</button>}
        </div>
      </section>}

      <section className="legal-history-card">
        <header><div><span className="legal-history-icon"><History size={15} /></span><span><small>03 · Revisionsspur</small><h2>Release-Historie</h2></span></div>
          {overview.active_generation?.prior_generation_id && <button type="button" onClick={rollback} disabled={!!busy}><RotateCcw size={14} /> Exakten Vorgänger wiederherstellen</button>}
        </header>
        {overview.releases.length === 0 ? <div className="legal-history-empty">Noch kein Release-Paket gespeichert.</div> : overview.releases.map(release => (
          <button className="legal-history-row" type="button" key={release.release_id} onClick={() => inspectRelease(release.release_id)}>
            <span><strong>{release.release_id}</strong><small>{release.effective_at} · {release.document_count} Dokumente</small></span>
            <em>{STATUS_LABEL[release.status] || release.status}</em>
          </button>
        ))}
      </section>
    </div>
  );
}
